import type { PortableTextBlock } from '@portabletext/react'
import { useEffect, useState } from 'react'

import type { PurchaseType } from '@/constants/programs'
import type {
  CMSProgramDaysAndMealsOption,
  CMSProgramDaysOption,
  CMSProgramFieldOption,
  CMSProgramMealsOption,
  CMSProgramOption,
  CMSProgramOptions,
  CMSProgramOptionVariant,
  CMSProgramPurchaseTypeOption,
  CMSProgramTab,
  CMSProgramWeeksOption,
} from '@/types/sanity'
import {
  getDaysOfFoodFromSku,
  getMealsArrayFromMealsString,
  getMealsArrayFromSku,
} from '@/utilities/sakara'

export type DynamicOptionGroupItem<T = CMSProgramOption> = {
  label: string
  tag?: string
  description?: PortableTextBlock[]
  isSelected: boolean
  option: T
  variant?: {
    sku: string
    price: number
  }
}

export type DynamicOptionGroup<T = CMSProgramOption> = {
  label?: string
  isHidden?: boolean
  optionsType: string
  options: DynamicOptionGroupItem<T>[]
}

export type SelectedOption<T = CMSProgramOption> = {
  label: string
  option: T
}

export type SelectedOptionsState = {
  purchaseType?: PurchaseType
  weeks?: number
  days?: number
  meals?: string[]
  purchaseTypeOption?: SelectedOption<CMSProgramPurchaseTypeOption>
  weeksOption?: SelectedOption<CMSProgramWeeksOption>
  daysOption?: SelectedOption<CMSProgramDaysOption>
  mealsOption?: SelectedOption<CMSProgramMealsOption>
  daysAndMealsOption?: SelectedOption<CMSProgramDaysAndMealsOption>
  activeVariant?: CMSProgramOptionVariant
}

export const useFlexibleProgram = (programTab: CMSProgramTab) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsState>(
    {},
  )
  const [dynamicOptionGroups, setDynamicOptionGroups] = useState<
    DynamicOptionGroup[]
  >([])

  useEffect(() => {
    programTab.associatedOption &&
      handleSelectedOptionChange({
        label: programTab.label,
        option: programTab.associatedOption,
      })
  }, [])

  const handleSelectedOptionChange = ({
    label,
    option,
  }: {
    label: string
    option: CMSProgramOption
  }) => {
    const newStatePart = getStatePartFromCMSOption({ label, option })!
    const newState = {
      ...selectedOptions,
      ...newStatePart,
    }
    const programOptions = programTab.sections?.find(
      (section) => section._type === 'web.program.programOptions',
    ) as CMSProgramOptions
    if (!programOptions) return

    const result = getDynamicOptionGroups(programOptions, newState)
    setSelectedOptions(result.newState)
    setDynamicOptionGroups(result.groups)
  }

  const getOptionKeyFromCMSType = (optionType: string) => {
    if (optionType === 'web.program.purchaseTypeOption') {
      return 'purchaseTypeOption'
    } else if (optionType === 'web.program.weeksOption') {
      return 'weeksOption'
    } else if (optionType === 'web.program.daysOption') {
      return 'daysOption'
    } else if (optionType === 'web.program.mealsOption') {
      return 'mealsOption'
    } else if (optionType === 'web.program.daysAndMealsOption') {
      return 'daysAndMealsOption'
    }
  }

  const getStatePartFromCMSOption = ({
    label,
    option,
  }: {
    label: string
    option: CMSProgramOption
  }) => {
    if (option._type === 'web.program.purchaseTypeOption') {
      return {
        purchaseType: option.value,
        purchaseTypeOption: { label, option },
      }
    } else if (option._type === 'web.program.weeksOption') {
      return {
        weeks: option.value,
        weeksOption: { label, option },
      }
    } else if (option._type === 'web.program.daysOption') {
      return {
        days: option.value,
        daysOption: { label, option },
        daysAndMealsOption: undefined,
      }
    } else if (option._type === 'web.program.mealsOption') {
      return {
        meals: getMealsArrayFromMealsString(option.value),
        mealsOption: { label, option },
        daysAndMealsOption: undefined,
      }
    } else if (option._type === 'web.program.daysAndMealsOption') {
      return {
        days: getDaysOfFoodFromSku(option.value),
        meals: getMealsArrayFromSku(option.value),
        daysAndMealsOption: { label, option },
        daysOption: undefined,
        mealsOption: undefined,
      }
    }
  }

  // TODO: REFACTOR THIS!!!
  const getDynamicOptionGroups = (
    dynamicField: {
      label?: string
      isHidden?: boolean
      optionsType: 'string'
      options: CMSProgramFieldOption[]
    },
    selectedOptions: SelectedOptionsState,
  ): {
    groups: DynamicOptionGroup[]
    newState: SelectedOptionsState
  } => {
    const { label, isHidden, optionsType, options } = dynamicField
    const optionKey = getOptionKeyFromCMSType(optionsType)!

    const selectedOption =
      options.find(
        (o) => o.option._id === selectedOptions[optionKey]?.option?._id,
      ) ||
      options.find((o) => o.selected) ||
      options[0]

    const optionGroup = {
      label,
      isHidden,
      optionsType,
      options: options.map((option) => ({
        label: option.label,
        tag: option.tag,
        description: option.description,
        isSelected: option.option._id === selectedOption.option._id,
        option: option?.option,
        variant: option?.variant,
      })),
    }

    const newStatePart = getStatePartFromCMSOption(selectedOption)
    const newState = {
      ...selectedOptions,
      ...newStatePart,
    }

    if (selectedOption?.childOptions) {
      const childOptionsResult = getDynamicOptionGroups(
        selectedOption.childOptions,
        newState,
      )
      return {
        groups: [optionGroup, ...childOptionsResult.groups],
        newState: { ...newState, ...childOptionsResult.newState },
      }
    }

    return {
      groups: [optionGroup],
      newState: { ...newState, activeVariant: selectedOption.variant },
    }
  }

  return {
    selectedOptions,
    dynamicOptionGroups,
    handleSelectedOptionChange,
  }
}
